// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

/* eslint-disable spire/fenced-imports */
import { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import ArrowNavigation from "@awc/App/Components/XngCarousel/ArrowNavigation";
import { xSwiperStyle } from "@awc/App/Components/XngCarousel/styles";
import { color } from "@awc/XDL/Utilities";
import { mergeWithCss } from "@awc/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import pick from "lodash/pick";
import React, { FC, isValidElement, useState } from "react";
import { css } from "styled-components";
import SwiperCore from "swiper";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);

export type Props = SwiperProps & {
    lazy?: boolean;
    extendedStyles?: SwiperStyle;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}

            .xng-pagination-container {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 12px;
                gap: 16px;

                & > div,
                & > button {
                    margin: 0;
                }
            }

            .xng-pagination-container > .swiper-scrollbar.swiper-scrollbar-horizontal {
                height: 7px;
                width: 100%;
                position: relative;
                top: unset;
                right: unset;
                bottom: unset;
                left: unset;
                transform: initial;
                background: ${color("common.ui2")};
                display: flex;
                align-items: center;

                & > .swiper-scrollbar-drag {
                    background: ${color("common.ui4")};
                    height: 3px;
                }
            }

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                gap: 8px 4px;

                &.swiper-pagination-lock {
                    display: none;
                }

                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    min-height: unset;
                    padding: 0;
                    margin: 0 !important;
                    border: 1px solid ${color("common.ui4")};
                    border-radius: 100%;
                    background: transparent;
                    opacity: 1;
                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("common.ui3")};
                        border-color: ${color("common.ui3")};
                        outline: none;
                    }
                    &.swiper-pagination-bullet-active {
                        width: 10px;
                        height: 10px;
                        background: ${color("common.ui5")};
                        border: 1px solid ${color("common.ui5")};
                    }
                }
            }
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const { children, className, extendedStyles, lazy = true, ...swiperProps } = props;
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));

    return (
        <StyledWrapper {...styles.container} className={`carousel-main-wrapper ${className || ""}`}>
            <Swiper {...swiperProps}>
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props as any, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps} data-test-selector="xngCarousel_swiperSlide">
                            {React.createElement(child.type, childrenProps)}
                            {lazy && <div className="swiper-lazy-preloader"></div>}
                        </SwiperSlide>
                    );
                })}
                {swiperProps.navigation && <ArrowNavigation arrowType="prev" showOnMobile={false} />}
                {swiperProps.navigation && <ArrowNavigation arrowType="next" showOnMobile={false} />}

                {(swiperProps.pagination || swiperProps.navigation || swiperProps.scrollbar) && (
                    <StyledWrapper className="xng-pagination-container">
                        {swiperProps.pagination && <div className="swiper-pagination" />}
                        {swiperProps.scrollbar && <div className="swiper-scrollbar" />}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

XngCarousel.defaultProps = {
    slidesPerView: "auto",
    navigation: true,
    simulateTouch: false,
    spaceBetween: 20,
    pagination: false,
    scrollbar: { draggable: true, el: ".swiper-scrollbar", dragSize: 50 },
    watchOverflow: true,
    updateOnWindowResize: true,
    centerInsufficientSlides: true,
    draggable: false,
    freeMode: false,
};

export default XngCarousel;
